import type {
  ExamSchedule as ExamScheduleData,
  ExamScheduleExam,
  ExamScheduleProgress,
} from '@mathflat/domain/Exam'
import type { AllGradeSubject, SchoolType } from '@mathflat/domain/SchoolSystem'
import { isAfter, isBefore } from 'date-fns'

import type { YearMonthStr } from '@/types'
import dateUtils from '@/utils/DateUtils'

export class ExamSchedule {
  year: number
  month: number
  round: number
  title: string
  progress: ExamScheduleProgress
  progressTimeline: {
    examPreOpenDatetime: Date
    examStartDatetime: Date
    examEndDatetime: Date
    gradingStartDatetime: Date
    gradingEndDatetime: Date
    awardRequestStartDatetime: Date | null
    awardRequestEndDatetime: Date | null
    examPdfDownloadStartDatetime: Date | null
    examPdfDownloadEndDatetime: Date | null
    examScheduleChanged: boolean
  }
  exams: ExamScheduleExam[]
  highSchoolProgressTimeline?: {
    examStartDatetime: Date
    examEndDatetime: Date
    gradingStartDatetime: Date
    gradingEndDatetime: Date
    awardRequestStartDatetime: Date | null
    awardRequestEndDatetime: Date | null
  }

  constructor(data: ExamScheduleData) {
    this.year = data.year
    this.month = data.month
    this.round = data.round
    this.title = data.title
    this.progress = data.progress
    this.exams = data.exams

    this.progressTimeline = {
      examPreOpenDatetime: new Date(data.progressTimeline.examPreOpenDatetime),
      examStartDatetime: new Date(data.progressTimeline.examStartDatetime),
      examEndDatetime: new Date(data.progressTimeline.examEndDatetime),
      gradingStartDatetime: new Date(data.progressTimeline.gradingStartDatetime),
      gradingEndDatetime: new Date(data.progressTimeline.gradingEndDatetime),
      awardRequestStartDatetime: data.progressTimeline.awardRequestStartDatetime
        ? new Date(data.progressTimeline.awardRequestStartDatetime)
        : null,
      awardRequestEndDatetime: data.progressTimeline.awardRequestEndDatetime
        ? new Date(data.progressTimeline.awardRequestEndDatetime)
        : null,
      examPdfDownloadStartDatetime: data.progressTimeline.examPdfDownloadStartDatetime
        ? new Date(data.progressTimeline.examPdfDownloadStartDatetime)
        : null,
      examPdfDownloadEndDatetime: data.progressTimeline.examPdfDownloadEndDatetime
        ? new Date(data.progressTimeline.examPdfDownloadEndDatetime)
        : null,
      examScheduleChanged: data.progressTimeline.examScheduleChanged,
    }
  }

  get yearMonth() {
    return `${this.year}-${this.month.toString().padStart(2, '0')}` as YearMonthStr<'-'>
  }

  get isCurrentExam() {
    const { examPreOpenDatetime, awardRequestEndDatetime, gradingEndDatetime } =
      this.progressTimeline
    const now = new Date()
    // 고등모의고사는 award가 없어 awardRequestEndDatetime이 null → gradingEndDatetime을 종료 기준으로 사용
    const endDatetime = awardRequestEndDatetime ?? gradingEndDatetime
    return dateUtils.checkIsWithin(now, examPreOpenDatetime, endDatetime)
  }

  get isExamPreOpen() {
    const { examPreOpenDatetime, examStartDatetime } = this.progressTimeline
    const now = new Date()

    return dateUtils.checkIsWithin(now, examPreOpenDatetime, examStartDatetime)
  }

  get isExamPdfDownloadable() {
    const { examPdfDownloadStartDatetime, examPdfDownloadEndDatetime } = this.progressTimeline

    if (!examPdfDownloadStartDatetime || !examPdfDownloadEndDatetime) {
      return false
    }

    const now = new Date()
    return dateUtils.checkIsWithin(now, examPdfDownloadStartDatetime, examPdfDownloadEndDatetime)
  }

  get isOpened() {
    const { examStartDatetime, examEndDatetime } = this.progressTimeline
    const now = new Date()

    return dateUtils.checkIsWithin(now, examStartDatetime, examEndDatetime)
  }

  get isResolved() {
    const { gradingEndDatetime } = this.progressTimeline
    const now = new Date()

    return isAfter(now, gradingEndDatetime)
  }

  get isBeforePreOpen() {
    const { examPreOpenDatetime } = this.progressTimeline
    const now = new Date()
    return isBefore(now, examPreOpenDatetime)
  }

  get isAfterPreOpen() {
    const { examPreOpenDatetime } = this.progressTimeline
    const now = new Date()
    return isAfter(now, examPreOpenDatetime)
  }

  get isExamScheduleChanged() {
    return this.progressTimeline.examScheduleChanged
  }

  get hasHighExam() {
    return this.exams.some((exam) => exam.schoolType === 'HIGH')
  }

  getExamByGradeSubject(schoolType: SchoolType, gradeSubject: AllGradeSubject) {
    return this.exams.find((exam) => {
      if (exam.schoolType === 'HIGH' && gradeSubject !== '1' && gradeSubject !== '2') {
        return exam.subject === gradeSubject
      }
      return exam.schoolType === schoolType && exam.grade.toString() === gradeSubject
    })
  }
}
